// "How do I …?" answers. The help prompt (inference_service NEXUS_HELP_SYSTEM,
// section "How-to questions") makes the model end such an answer with a
// fenced ```nexus-guide block:
//
//   {"title": "Conectar un Excel",
//    "steps": [{"page": "connectors", "target_text": "Nueva fuente",
//               "title": "Abre Fuentes de datos", "body": "…"}]}
//
// The chat renders it as a numbered card, and "Mostrarme en pantalla" runs it
// through the tour overlay: each step navigates to `page` and spotlights the
// control labelled `target_text`. Everything here is model output, so it is
// validated: unknown pages are dropped (the step still shows, centered),
// over-long lists are cut.

import type { TourStep } from '../tour/steps';
import { PAGE_ALIASES, routeForPage } from './routes';

export interface GuideStep {
  title: string;
  body: string;
  /** Page id from shell/routes.ts, validated. */
  page?: string;
  /** Visible label of the control to click on that page. */
  targetText?: string;
}

export interface AssistantGuide {
  title: string;
  steps: GuideStep[];
}

export const GUIDE_LANG = 'nexus-guide';
const MAX_STEPS = 12;
const FENCE = /```nexus-guide[^\n]*\n([\s\S]*?)```/;

const str = (v: unknown, max: number): string =>
  typeof v === 'string' ? v.trim().slice(0, max) : '';

function knownPage(raw: unknown): string | undefined {
  const page = str(raw, 64);
  if (!page) return undefined;
  const resolved = PAGE_ALIASES[page] ?? page;
  return routeForPage(resolved) ? resolved : undefined;
}

/** The guide in an assistant message, or null when there is none (or it is
 *  still streaming: an unclosed fence does not match). */
export function extractGuide(text: string): AssistantGuide | null {
  const m = FENCE.exec(text);
  if (!m) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(m[1]);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as { title?: unknown; steps?: unknown };
  if (!Array.isArray(obj.steps)) return null;
  const steps: GuideStep[] = [];
  for (const s of obj.steps.slice(0, MAX_STEPS)) {
    if (!s || typeof s !== 'object') continue;
    const r = s as Record<string, unknown>;
    const title = str(r.title, 120);
    const body = str(r.body, 600);
    if (!title && !body) continue;
    steps.push({
      title: title || body.slice(0, 60),
      body,
      page: knownPage(r.page),
      targetText: str(r.target_text ?? r.targetText, 80) || undefined,
    });
  }
  if (steps.length === 0) return null;
  return { title: str(obj.title, 120) || 'Guía paso a paso', steps };
}

/** Tour overlay steps for a guide. */
export function guideToTourSteps(guide: AssistantGuide): TourStep[] {
  return guide.steps.map((s, i) => ({
    id: `guide-${i}`,
    page: s.page,
    targetText: s.targetText,
    title: s.title,
    body: s.body,
    action: s.targetText ? `[Haz clic en "${s.targetText}"]` : undefined,
    // Clicking the highlighted control moves on, like the demo's tile steps.
    advanceOnTargetClick: !!s.targetText,
  }));
}
